import type { AlertConfiguration } from '../types/dashboard';

/** Left accent colors aligned with desktop Alerts list (DMTA + linked Outs). */
export const ALERT_LIST_ACCENT_PALETTE = [
  '#0078d4',
  '#107c10',
  '#b74700',
  '#803988',
  '#006f79',
  '#a30f5a',
] as const;

export interface AlertConfigurationListRow {
  config: AlertConfiguration;
  isGroupedChild: boolean;
  /** Index into ALERT_LIST_ACCENT_PALETTE, or -1 when no group accent */
  accentColorIndex: number;
  showDelete: boolean;
}

/** Backend id may be string or number in responses. */
export function resolveAlertNumericId(c: AlertConfiguration): number {
  const raw = c.id as string | number;
  if (typeof raw === 'number' && !Number.isNaN(raw)) return raw;
  const n = parseInt(String(raw), 10);
  return Number.isNaN(n) ? 0 : n;
}

function linkedParentId(c: AlertConfiguration): number | null {
  const v =
    c.linked_directional_alert_id ??
    (c as AlertConfiguration & { linkedDirectionalAlertId?: number | string }).linkedDirectionalAlertId;
  if (v == null) return null;
  if (typeof v === 'string' && v.trim() === '') return null;
  const n = typeof v === 'number' ? v : parseInt(String(v), 10);
  return Number.isNaN(n) ? null : n;
}

/** Single-trigger Out row linked to a directional multi-trigger parent (same rule as desktop). */
export function isDirectionalLinkedOutAlert(c: AlertConfiguration): boolean {
  const pos = (c.position_type || c.positionType || '').trim().toLowerCase();
  return pos === 'out' && linkedParentId(c) != null;
}

const DIRECTIONAL_MULTI_POSITION_TYPES = new Set([
  'breakout',
  'reversal',
  'sideways/ranging',
  'other',
  'up',
  'down',
  'sideways',
]);

/** Resolve stored alert kind (matches backend effective_stored_kind for list/monitor UI). */
export function effectiveAlertKindFromConfig(c: AlertConfiguration): string {
  const raw =
    c.alert_kind ?? (c as AlertConfiguration & { alertKind?: string }).alertKind;
  const kind = String(raw ?? '').trim().toLowerCase();
  if (
    kind === 'bidirectional_multi' ||
    kind === 'directional_multi' ||
    kind === 'single' ||
    kind === 'legacy_multi'
  ) {
    return kind;
  }
  if (c.is_multi_trigger) {
    const pt = (c.position_type || c.positionType || '').trim().toLowerCase();
    if (DIRECTIONAL_MULTI_POSITION_TYPES.has(pt)) return 'directional_multi';
  }
  if (kind === 'single') return 'directional_multi';
  return c.is_multi_trigger ? 'directional_multi' : 'directional_multi';
}

/**
 * Parent row is a directional multi-trigger alert (DMTA).
 * Matches backend effective_stored_kind + AlertsPage.kindFromAlert: if alert_kind is missing
 * but is_multi_trigger is true with a directional position type, still treat as DMTA so
 * linked Out rows nest correctly in the list.
 */
export function isDirectionalMultiParentConfig(c: AlertConfiguration): boolean {
  const raw =
    c.alert_kind ?? (c as AlertConfiguration & { alertKind?: string }).alertKind;
  const kind = String(raw ?? '').trim().toLowerCase();
  if (kind === 'bidirectional_multi') return false;
  if (kind === 'directional_multi') return true;
  if (kind === 'single' || kind === 'legacy_multi') return false;
  if (c.is_multi_trigger) {
    const pt = (c.position_type || c.positionType || '').trim().toLowerCase();
    if (DIRECTIONAL_MULTI_POSITION_TYPES.has(pt)) return true;
  }
  return false;
}

export interface AlertRootGroup {
  root: AlertConfiguration;
  children: AlertConfiguration[];
}

/**
 * Group each DMTA with its linked Out rows in the same list.
 * Outs only nest under a parent whose alert_kind is directional_multi.
 * Orphan Outs (parent missing or not DMTA) stay as top-level roots.
 */
export function groupAlertConfigurationsForList(configs: AlertConfiguration[]): AlertRootGroup[] {
  const list = [...configs];
  const idSet = new Set(list.map((c) => resolveAlertNumericId(c)));
  const byNumericId = new Map<number, AlertConfiguration>();
  for (const c of list) {
    byNumericId.set(resolveAlertNumericId(c), c);
  }

  const roots = list.filter((c) => {
    if (!isDirectionalLinkedOutAlert(c)) return true;
    const pid = linkedParentId(c);
    if (pid == null || !idSet.has(pid)) return true;
    const parent = byNumericId.get(pid);
    if (!parent || !isDirectionalMultiParentConfig(parent)) return true;
    return false;
  });

  roots.sort((a, b) => {
    const da = new Date(a.created_at || a.createdAt || 0).getTime();
    const db = new Date(b.created_at || b.createdAt || 0).getTime();
    return db - da;
  });

  return roots.map((root) => ({ root, children: [] as AlertConfiguration[] }));
}

/** Assign accent indices so every row has a bar and no two adjacent rows share the same color. */
export function assignDistinctAccentIndices(
  rows: Omit<AlertConfigurationListRow, 'accentColorIndex'>[],
): AlertConfigurationListRow[] {
  const paletteLen = ALERT_LIST_ACCENT_PALETTE.length;
  let prev = -1;
  return rows.map((row, i) => {
    const id = resolveAlertNumericId(row.config);
    let accent = Math.abs(id + i) % paletteLen;
    if (accent === prev) {
      accent = (accent + 1) % paletteLen;
    }
    prev = accent;
    return { ...row, accentColorIndex: accent };
  });
}

/**
 * Flat list for Alerts tab: one row per alert (linked Out children are hidden; exits live on parent webhooks).
 */
export function flattenAlertListRows(groups: AlertRootGroup[]): AlertConfigurationListRow[] {
  const base: Omit<AlertConfigurationListRow, 'accentColorIndex'>[] = [];
  for (const { root } of groups) {
    base.push({
      config: root,
      isGroupedChild: false,
      showDelete: true,
    });
  }
  return assignDistinctAccentIndices(base);
}

/** Count linked Out rows under this parent id (for delete confirmation copy). */
export function countLinkedOutChildren(configs: AlertConfiguration[], parentNumericId: number): number {
  return configs.filter((c) => linkedParentId(c) === parentNumericId).length;
}
